import Tabbar from '@components/Tabbar';
import Title from '@components/Title';
import { deleteComponentLayers } from '@modules/componentLayer/actions';
import { $sceneMapApi } from '@stores/dataStores';
import {
  $GateLayerAnimationTab,
  $editingComponentLayer,
  $editingComponentLayerApi,
  type EditingCLayerStore,
} from '@stores/editingComponentLayerStores';
import { $viewModeDisplayApi } from '@stores/viewModeStores';
import type { ComponentLayerAnimationCategory } from '@types';
import cn from '@utils/cn';
import { useGate, useStore } from 'effector-react';
import { useEffect, useState } from 'react';
import useConstant from 'use-constant';
import PanelBase from '../PanelBase';
import { LayerActionSettings } from './LayerActionSettings';
import { LayerAnimations } from './LayerAnimations';
import { LayerControlSettings } from './LayerControlSettings';
import { LayerStyleSettings } from './LayerStyleSettings';

function PanelComponentLayer() {
  const editing = useStore($editingComponentLayer);
  if (!editing) return null;
  if (!editing.layer) return null;
  return <PanelComponentLayerMain editing={editing} />;
}

const getAnimationCategory = (
  tabIndex: number
): ComponentLayerAnimationCategory => {
  if (tabIndex === 0) return 'enterAnimation';
  if (tabIndex === 2) return 'exitAnimation';
  return 'animation';
};

function PanelComponentLayerMain({ editing }: { editing: EditingCLayerStore }) {
  const { layer, sceneId, isNew } = editing;
  const firstState = useConstant(() => layer);
  const [topTab, setTopTab] = useState(0);
  const [interactionTab, setInteractionTab] = useState(0);
  const [animationTab, setAnimationTab] = useState(1);

  useGate($GateLayerAnimationTab, getAnimationCategory(animationTab));

  useEffect(() => {
    $viewModeDisplayApi.hide();
    return () => {
      $editingComponentLayerApi.clear();
      $viewModeDisplayApi.show();
    };
  }, []);

  const handleClose = () => {
    $editingComponentLayerApi.set(null);
  };

  const handleDelete = () => {
    if (!isNew) {
      deleteComponentLayers({ sceneId, ids: layer.id });
    }
  };

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.config.components ??= [];
        const list = draft.config.components;
        const index = list.findIndex((x) => x.id === layer.id);
        if (index > -1) {
          list[index] = layer;
        } else {
          list.push(layer);
        }
      },
    });
  };

  return (
    <PanelBase
      isChanged={() => firstState !== layer}
      title={<Title>{isNew ? '添加图层' : '编辑图层'}</Title>}
      onRequestClose={handleClose}
      onConfirm={handleConfirm}
      onDeleteClick={handleDelete}
    >
      <div className="sticky top-0 z-10 bg-black pb-10">
        <Tabbar
          tabs={['样式', '动画', '交互']}
          activeIndex={topTab}
          onChange={(index) => {
            setTopTab(index);
            setAnimationTab(1);
          }}
        />
      </div>
      <div className={cn({ hidden: topTab !== 0 })}>
        <LayerStyleSettings data={layer} />
      </div>
      <div className={cn({ hidden: topTab !== 1 })}>
        <div className="sticky top-45 z-10 bg-black pb-10">
          <Tabbar
            tabs={['进入', '停留', '退出']}
            activeIndex={animationTab}
            onChange={(index) => setAnimationTab(index)}
          />
        </div>
        <div className={cn({ hidden: animationTab !== 0 })}>
          <LayerAnimations
            category="enterAnimation"
            animation={layer.enterAnimation}
          />
        </div>
        <div className={cn({ hidden: animationTab !== 1 })}>
          <LayerAnimations category="animation" animation={layer.animation} />
        </div>
        <div className={cn({ hidden: animationTab !== 2 })}>
          <LayerAnimations
            category="exitAnimation"
            animation={layer.exitAnimation}
          />
        </div>
      </div>
      <div className={cn({ hidden: topTab !== 2 })}>
        <div className="sticky top-45 z-10 bg-black pb-10">
          <Tabbar
            tabs={['触发功能', '图层控制']}
            activeIndex={interactionTab}
            onChange={(index) => setInteractionTab(index)}
          />
        </div>
        <div className={cn({ hidden: interactionTab !== 0 })}>
          <LayerActionSettings data={layer} />
        </div>
        <div className={cn({ hidden: interactionTab !== 1 })}>
          <LayerControlSettings data={layer} />
        </div>
      </div>
    </PanelBase>
  );
}

export default PanelComponentLayer;
